import { useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import CollapsibleSection from './CollapsibleSection';
import SourcesSection from './DealDetail/SourcesSection';
import AMFIEditor from './AMFI/AMFIEditor';
import { useParams } from 'react-router';
import { AMFI_TYPES, getAmfiConfig } from '../constants/amfiConfig';

/*
 * UnifiedTabPanel component to display tab content
 * @param {string} tabType - The type of tab (category, tasks, team, etc)
 * @param {string} tabKey - The key/id of the tab
 * @param {object} data - The data to display in the tab
 * @param {string} title - The title of the tab
 */

// TODO: use tabKey and title props, and remove linter disabler, or remove them
// eslint-disable-next-line no-unused-vars
const UnifiedTabPanel = ({ tabType, tabKey, data, title, categoryItems }) => {
	// Get tenantId and dealId from URL params
	const { tenantId, dealId } = useParams();

	// Store references to create functions from AMFIEditor components
	const createFunctionsRef = useRef({
		attribute: null,
		metric: null,
		fact: null,
		insight: null,
	});

	// State for accordion expansion
	const [expandedSections, setExpandedSections] = useState({
		attributes: true, // Default open
		metrics: false,
		facts: false,
		insights: false,
	});

	// Handler for accordion expansion changes
	const handleAccordionChange = (section) => (_event, isExpanded) => {
		setExpandedSections({
			...expandedSections,
			[section]: isExpanded,
		});
	};

	if (!data) {
		return (
			<Box>
				<Typography variant='body1'>No data available</Typography>
			</Box>
		);
	}

	const renderInformationContent = () => {
		const { attributes, metrics, facts, insights } = data;
		const hasData =
			attributes?.length > 0 ||
			metrics?.length > 0 ||
			facts?.length > 0 ||
			insights?.length > 0;

		if (!hasData) {
			return (
				<Box sx={{ my: 2 }}>
					<Typography variant='body1'>
						No data available for this category
					</Typography>
				</Box>
			);
		}

			// Handler to store the create function reference
		const handleCreateRefReady = (type) => (createFn) => {
			createFunctionsRef.current[type] = createFn;
		};

		// Handler for create button
		const handleOpenCreateItem = (type) => () => {
			if (createFunctionsRef.current[type]) {
				createFunctionsRef.current[type]();
			} else {
				console.log(`Create function for ${type} not available yet`);
			}
		};

		return (
			<Box>
				{AMFI_TYPES.map((type) => {
					const sectionKey = `${type}s`;
					const config = getAmfiConfig(type);

					return (
						<CollapsibleSection
							key={type}
							title={config.pluralDisplayName}
							icon={config.icon}
							color={config.color}
							itemCount={data[sectionKey]?.length || 0}
							isExpanded={expandedSections[sectionKey]}
							onToggle={() =>
								handleAccordionChange(sectionKey)(
									null,
									!expandedSections[sectionKey]
								)
							}
							onOpenCreate={handleOpenCreateItem(type)}
						>
							<AMFIEditor
								tenantId={tenantId}
								dealId={dealId}
								type={type}
								categories={categoryItems || []}
								items={data[sectionKey] || []}
								hideTitle={true}
								onCreateRefReady={handleCreateRefReady(type)}
							/>
						</CollapsibleSection>
					);
				})}
			</Box>
		);
	};

	const renderTasksContent = () => {
		const { tasks } = data;
		return (
			<Box>
				<Typography variant='h6'>Tasks</Typography>
				{tasks?.length > 0 ? (
					<Box sx={{ mt: 2 }}>
						{tasks.map((task) => (
							<Box key={task.id} sx={{ mb: 1 }}>
								<Typography variant='body1'>{task.title}</Typography>
							</Box>
						))}
					</Box>
				) : (
					<Typography variant='body1'>No tasks available</Typography>
				)}
			</Box>
		);
	};

	// Render the tab content based on tab type
	const renderTabContent = () => {
		switch (tabType) {
			case 'sources':
				return (
					<SourcesSection
						sources={data.sources || []}
						promptLibrary={data.promptLibrary || []}
					/>
				);
			case 'information':
				return renderInformationContent();
			case 'tasks':
				return renderTasksContent();
			case 'team':
				return <Typography>Team content goes here</Typography>;
			case 'monitors':
				return <Typography>Monitors content goes here</Typography>;
			case 'interactions':
				return <Typography>Interactions content goes here</Typography>;
			default:
				return <Typography>Tab content not available</Typography>;
		}
	};

	return <Box>{renderTabContent()}</Box>;
};

export default UnifiedTabPanel;
